/**
 * Zapier API Key Service
 * Manages API keys for Zapier integration
 */

import { fetchAuthSession } from 'aws-amplify/auth';

// BigSplice Zapier API endpoint
const ZAPIER_API_KEY_ENDPOINT = 'https://sxfa57ty73.execute-api.us-east-1.amazonaws.com/prod/zapier/api-keys';

export interface ZapierApiKey {
  key_id: string;
  api_key?: string; // Only returned on create
  name: string;
  created_at: string;
  last_used_at?: string;
  is_active: boolean;
}

export interface CreateKeyResponse {
  success: boolean;
  message: string;
  key_id: string;
  name: string;
  created_at: string;
  api_key: string; // Full key only returned once
}

export interface ListKeysResponse {
  success: boolean;
  keys: ZapierApiKey[];
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  } catch (error) {
    console.error('Error getting auth headers:', error);
    return { 'Content-Type': 'application/json' };
  }
}

export const zapierApiKeyService = {
  /**
   * Create a new API key for Zapier
   */
  async createKey(name: string): Promise<CreateKeyResponse> {
    const headers = await getAuthHeaders();

    const response = await fetch(ZAPIER_API_KEY_ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create API key' }));
      throw new Error(error.error || 'Failed to create API key');
    }

    return response.json();
  },

  /**
   * List all API keys for the current user
   */
  async listKeys(): Promise<ZapierApiKey[]> {
    const headers = await getAuthHeaders();

    const response = await fetch(ZAPIER_API_KEY_ENDPOINT, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to list API keys' }));
      throw new Error(error.error || 'Failed to list API keys');
    }

    const data: ListKeysResponse = await response.json();
    return data.keys || [];
  },

  /**
   * Revoke an API key
   */
  async revokeKey(keyId: string): Promise<void> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${ZAPIER_API_KEY_ENDPOINT}?key_id=${encodeURIComponent(keyId)}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to revoke API key' }));
      throw new Error(error.error || 'Failed to revoke API key');
    }
  },

  /**
   * Format a date string for display
   */
  formatDate(dateString: string | undefined): string {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  },

  /**
   * Mask an API key for display
   */
  maskKey(key: string): string {
    if (!key || key.length < 12) return '••••••••';
    return `${key.substring(0, 6)}...${key.substring(key.length - 4)}`;
  },
};
